import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon";
import "./SearchSelect.css";

export type SearchSelectOption = {
  id: string;
  label: string;
  sub?: string;
  mark?: ReactNode;
  badge?: ReactNode;
};

type Coords = {
  left: number;
  width: number;
  top: number | null;
  bottom: number | null;
};

interface Props {
  value: string | null | undefined;
  onChange: (id: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  searchKeys?: Array<keyof SearchSelectOption>;
  maxHeight?: number;
  emptyLabel?: string;
  disabled?: boolean;
}

const PANEL_MAX = 320;

export function SearchSelect({
  value,
  onChange,
  options = [],
  placeholder = "Selecione…",
  searchKeys = ["label", "sub"],
  maxHeight = 280,
  emptyLabel = "Nenhum resultado encontrado.",
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [coords, setCoords] = useState<Coords | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.id === value) ?? null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) =>
      searchKeys.some((k) =>
        String((o as Record<string, unknown>)[k as string] ?? "")
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [options, q, searchKeys]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const flipUp = spaceBelow < PANEL_MAX && spaceAbove > spaceBelow;
    setCoords({
      left: r.left,
      width: r.width,
      top: flipUp ? null : r.bottom + 6,
      bottom: flipUp ? window.innerHeight - r.top + 6 : null,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    setQ("");
    setActiveIdx(0);
    reposition();
    setTimeout(() => inputRef.current?.focus(), 0);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    if (!item) return;
    const list = listRef.current;
    const itemTop = item.offsetTop;
    const itemBot = itemTop + item.offsetHeight;
    if (itemTop < list.scrollTop) list.scrollTop = itemTop;
    else if (itemBot > list.scrollTop + list.clientHeight)
      list.scrollTop = itemBot - list.clientHeight;
  }, [activeIdx, open]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) {
        onChange(opt.id);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const panelStyle: CSSProperties = coords
    ? {
        left: coords.left,
        width: coords.width,
        top: coords.top != null ? coords.top : "auto",
        bottom: coords.bottom != null ? coords.bottom : "auto",
        maxHeight: PANEL_MAX,
      }
    : { visibility: "hidden" };

  const panel = (
    <div className="ss-panel" style={panelStyle} ref={panelRef}>
      <div className="ss-search">
        <Icon name="search" size={14} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Buscar…"
        />
        {q && (
          <button type="button" className="ss-clear" onClick={() => setQ("")}>
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      <div className="ss-list" ref={listRef} style={{ maxHeight }}>
        {filtered.length === 0 ? (
          <div className="ss-empty">
            <Icon name="search" size={16} />
            <span>{emptyLabel}</span>
          </div>
        ) : (
          filtered.map((o, i) => (
            <button
              key={o.id}
              type="button"
              data-idx={i}
              className={`ss-opt ${o.id === value ? "on" : ""} ${i === activeIdx ? "active" : ""}`}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              {o.mark != null && <span className="ss-mark">{o.mark}</span>}
              <span className="ss-opt-l">
                <span className="ss-opt-label">{o.label}</span>
                {o.sub && <span className="ss-opt-sub">{o.sub}</span>}
              </span>
              {o.badge != null && <span className="ss-opt-badge">{o.badge}</span>}
              {o.id === value && <Icon name="check" size={13} className="ss-opt-check" />}
            </button>
          ))
        )}
      </div>

      <div className="ss-foot">
        {filtered.length === options.length ? (
          <span>
            {options.length} {options.length === 1 ? "opção" : "opções"}
          </span>
        ) : (
          <span>
            {filtered.length} de {options.length}
          </span>
        )}
        <span className="ss-foot-hint">↑↓ navegar · ↵ selecionar · esc fechar</span>
      </div>
    </div>
  );

  return (
    <div className="search-select">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={`search-select-trigger ${open ? "open" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {selected ? (
          <span className="ss-selected">
            {selected.mark != null && <span className="ss-mark">{selected.mark}</span>}
            <span className="ss-selected-label">
              <span className="ss-selected-l">{selected.label}</span>
              {selected.sub && <span className="ss-selected-s">{selected.sub}</span>}
            </span>
          </span>
        ) : (
          <span className="ss-placeholder">{placeholder}</span>
        )}
        <Icon name="chevron-down" size={14} className="ss-caret" />
      </button>

      {open && createPortal(panel, document.body)}
    </div>
  );
}
